import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardBody,
  V2CardHeader,
  V2ConfirmDialog,
} from '@sakhtar/design-system/react';
import { ApiError, apiDelete, apiPut } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useApiGet } from '../../lib/useApiGet.js';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — `/t/kimia/master-data` (module header + V2Card
 *   sections). The «مهر و امضا» card set is not in the frame; it follows the same card
 *   language as the other master-data pages.
 *
 * Logo / seal / signature used on the printed pre-invoice (پیش‌فاکتور). Read access is
 * `master.brand-asset.read`; upload needs `master.brand-asset.create`/`.update`, removal `.delete`, and the API
 * enforces both (the buttons are only hidden when `canManage` is false).
 */

type Kind = 'logo' | 'seal' | 'signature';

interface BrandAssetItem {
  readonly kind: Kind;
  readonly label: string;
  readonly configured: boolean;
  readonly source: 'tenant' | 'signup' | null;
  readonly dataUrl: string | null;
  readonly updatedAt: string | null;
}

interface BrandAssetsResponse {
  readonly canManage: boolean;
  readonly items: readonly BrandAssetItem[];
}

const HINTS: Readonly<Record<Kind, string>> = {
  logo: 'در سربرگ پیش‌فاکتور نمایش داده می‌شود. اگر چیزی بارگذاری نکنید، لوگوی زمان ساخت سازمان استفاده می‌شود.',
  seal: 'در جای «مهر» پایین پیش‌فاکتور چاپ می‌شود. تصویر با پس‌زمینهٔ شفاف (PNG) بهترین نتیجه را می‌دهد.',
  signature:
    'در جای «امضا» پایین پیش‌فاکتور چاپ می‌شود. تصویر با پس‌زمینهٔ شفاف (PNG) بهترین نتیجه را می‌دهد.',
};

const MAX_BYTES = 512_000;

function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

function AssetCard({
  item,
  canManage,
  base,
  onChanged,
}: {
  item: BrandAssetItem;
  canManage: boolean;
  base: string;
  onChanged: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const path = `${base}/api/v1/master-data/brand-assets/${item.kind}`;

  async function onFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    if (file.size > MAX_BYTES) {
      setError('حجم تصویر نباید بیشتر از ۵۰۰ کیلوبایت باشد.');
      return;
    }
    setBusy(true);
    try {
      await apiPut(path, { dataUrl: await readFile(file) });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.envelope.error.message : 'بارگذاری ناموفق بود.');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await apiDelete(path);
      setConfirmRemove(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.envelope.error.message : 'حذف ناموفق بود.');
      setConfirmRemove(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <V2Card>
      <V2CardHeader
        title={item.label}
        subtitle={HINTS[item.kind]}
        actions={
          item.configured ? (
            <V2Badge tone={item.source === 'signup' ? 'info' : 'success'}>
              {item.source === 'signup' ? 'لوگوی زمان ثبت‌نام' : 'تنظیم شده'}
            </V2Badge>
          ) : (
            <V2Badge tone="neutral">تنظیم نشده</V2Badge>
          )
        }
      />
      <V2CardBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              minHeight: 120,
              display: 'grid',
              placeItems: 'center',
              border: '1px dashed var(--sk-border)',
              borderRadius: 'var(--sk-radius-md, 8px)',
              background: 'var(--sk-surface-muted, transparent)',
              padding: 12,
            }}
          >
            {item.dataUrl ? (
              <img
                src={item.dataUrl}
                alt={item.label}
                style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }}
              />
            ) : (
              <span style={{ color: 'var(--sk-fg-muted)', fontSize: 13 }}>
                تصویری بارگذاری نشده است
              </span>
            )}
          </div>
          {error ? <V2Alert tone="danger">{error}</V2Alert> : null}
          {canManage ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                ref={input}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                hidden
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
              <V2Button
                variant="primary"
                icon="upload"
                disabled={busy}
                onClick={() => input.current?.click()}
              >
                {item.dataUrl ? 'جایگزینی تصویر' : 'بارگذاری تصویر'}
              </V2Button>
              {item.source === 'tenant' ? (
                <V2Button variant="ghost" disabled={busy} onClick={() => setConfirmRemove(true)}>
                  حذف
                </V2Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </V2CardBody>
      <V2ConfirmDialog
        open={confirmRemove}
        danger
        loading={busy}
        title={`حذف ${item.label}`}
        message={`${item.label} از پیش‌فاکتورهای بعدی حذف می‌شود. ادامه می‌دهید؟`}
        confirmLabel="حذف"
        cancelLabel="انصراف"
        onConfirm={() => void remove()}
        onCancel={() => setConfirmRemove(false)}
      />
    </V2Card>
  );
}

export function BrandAssetsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refresh, setRefresh] = useState(0);
  const res = useApiGet<BrandAssetsResponse>(
    slug ? `${base}/api/v1/master-data/brand-assets` : null,
    [slug, refresh],
  );

  if (!slug) return null;
  if (res.status === 'loading') return <LoadingState />;
  if (res.status === 'error') {
    return (
      <StatePanel
        variant={res.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={
          res.error.status === 403 ? 'دسترسی به مهر و امضا ندارید' : 'مهر و امضا قابل نمایش نیست'
        }
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {!res.data.canManage ? (
        <V2Alert tone="info">
          فقط مشاهده می‌کنید. برای بارگذاری یا تغییر مهر و امضا، مجوز «مدیریت مهر و امضا» را از مدیر
          سازمان بخواهید.
        </V2Alert>
      ) : null}
      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        }}
      >
        {res.data.items.map((item) => (
          <AssetCard
            key={item.kind}
            item={item}
            canManage={res.data.canManage}
            base={base}
            onChanged={() => setRefresh((n) => n + 1)}
          />
        ))}
      </div>
    </div>
  );
}
